/**
 * The team screen: who is at the studio.
 *
 * It owns the people. Who exists, what they are called, what their job is, the paragraph on
 * their card, their portrait, and the year they started in games — which is the number the card
 * prints as a level. What any of them did on a particular game is not here: that is a credit,
 * it belongs to the pairing rather than to the person, and it is edited on the game itself
 * under Team, beside everything else about that game.
 *
 * Each person is a form that saves on its own. There is no shared toolbar because there is no
 * shared draft: editing Anthony's biography and Jeremy's job title are two unrelated writes,
 * and a single Save across both would make every edit a transaction nobody asked for.
 */

import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CAREER_START_MIN,
  ROLE_BY_ID,
  yearsOfExperience,
  type AdminTeamMember,
} from '@goodbuild/shared';
import { Badge, Button, Callout, Card, Eyebrow, Heading, Text } from '../ui/index.js';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import { useAdminTeam } from '../components/admin/teamFields.js';
import { slugify, SLUG_RULE } from '../components/admin/gameFields.js';
import { readImageSize, uploadImage } from '../components/game/GameArtwork.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatRelative } from '../lib/format.js';
import { errorMessage } from '../lib/errors.js';

export const Route = createFileRoute('/admin/team')({ component: AdminTeam });

/** The four the API signs an upload for. A GIF is refused before it is read. */
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/avif'];

function AdminTeam() {
  const qc = useQueryClient();
  const { isAdmin } = useAccess();
  const team = useAdminTeam();
  const [error, setError] = React.useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.admin.team });
    // The studio page reads the same table, and every game carries its roll.
    void qc.invalidateQueries({ queryKey: keys.team });
    void qc.invalidateQueries({ queryKey: keys.games });
  };

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;
  if (team.isLoading) return <Loading />;

  const members = team.data ?? [];

  return (
    <div className="gb-stack">
      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}

      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">{'The team'}</Heading>
        <Text size="2" color="gray">
          {
            'These are the cards on the studio screen. What somebody did on a given game is set on that game, under Team.'
          }
        </Text>
      </Card>

      <AddMember onAdded={refresh} onError={setError} existing={members} />

      <FocusZone focusKey="admin-team" direction="column" className="gb-stack">
        {members.length === 0 ? (
          <EmptyState
            title={'Nobody yet.'}
            body={'Add the first person above. The studio screen lists them the moment you do.'}
          />
        ) : (
          members.map((member) => (
            <MemberEditor key={member.id} member={member} onChanged={refresh} />
          ))
        )}
      </FocusZone>
    </div>
  );
}

/* -------------------------------------------------------------- adding one */

/**
 * The four things a person cannot be created without.
 *
 * A name, a job, an address and nothing else — everything else on a card is filled in on the
 * row below once they exist, the same way a game is created with a name and an address and
 * filled in afterwards. A create form that asked for a biography would be a form nobody uses.
 */
function AddMember({
  existing,
  onAdded,
  onError,
}: {
  existing: AdminTeamMember[];
  onAdded: () => void;
  onError: (message: string | null) => void;
}) {
  const [name, setName] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  // The address follows the name until somebody types one, which is the behaviour the game
  // create form already has and the one nobody has to be told about.
  const address = touched ? slug : slugify(name);
  const taken = existing.some((member) => member.slug === address);
  const valid = Boolean(name.trim() && title.trim() && SLUG_RULE.test(address) && !taken);

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/team', {
        slug: address,
        name: name.trim(),
        title: title.trim(),
        // Last, so a new person lands at the end of the row rather than at the front of it.
        sortOrder: existing.length,
      }),
    onSuccess: () => {
      setName('');
      setTitle('');
      setSlug('');
      setTouched(false);
      onError(null);
      onAdded();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  return (
    <Card size="3" className="gb-form">
      <Eyebrow small>{'Add somebody'}</Eyebrow>
      <div className="gb-two">
        <FocusableTextField value={name} onChange={setName} label={'Name'} maxLength={80} />
        <FocusableTextField
          value={title}
          onChange={setTitle}
          label={'Job title'}
          placeholder={'Senior Level Designer'}
          maxLength={80}
        />
      </div>
      <FocusableTextField
        value={address}
        onChange={(next) => {
          setTouched(true);
          setSlug(next);
        }}
        label={'Address'}
        placeholder={'jane-doe'}
        maxLength={64}
        hint={
          taken
            ? 'Somebody already has that address.'
            : 'Lowercase letters, numbers and single hyphens. It is also how the app matches a bundled portrait.'
        }
      />
      <div className="gb-row">
        <Focusable
          onSelect={() => create.mutate()}
          selectLabel={'Add'}
          disabled={!valid || create.isPending}
        >
          <Button size="2" disabled={!valid || create.isPending}>
            {create.isPending ? 'Adding' : 'Add to the team'}
          </Button>
        </Focusable>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------- editing one */

interface Draft {
  name: string;
  title: string;
  bio: string;
  careerStartYear: string;
  location: string;
  sortOrder: string;
}

function draftOf(member: AdminTeamMember): Draft {
  return {
    name: member.name,
    title: member.title,
    bio: member.bio,
    // A number as a string, because that is what the field holds and a half-typed year is a
    // state the form has to be able to be in.
    careerStartYear: member.careerStartYear === null ? '' : String(member.careerStartYear),
    location: member.location ?? '',
    sortOrder: String(member.sortOrder),
  };
}

/** What is wrong with the draft, in the words the fields show. Empty means it can be saved. */
function problemsIn(draft: Draft): string[] {
  const problems: string[] = [];
  if (!draft.name.trim()) problems.push('A person needs a name.');
  if (!draft.title.trim()) problems.push('A person needs a job title.');

  const year = draft.careerStartYear.trim();
  if (year) {
    const parsed = Number(year);
    const thisYear = new Date().getUTCFullYear();
    if (!Number.isInteger(parsed) || parsed < CAREER_START_MIN || parsed > thisYear) {
      problems.push(
        `The year they started is between ${String(CAREER_START_MIN)} and ${String(thisYear)}.`,
      );
    }
  }
  if (draft.sortOrder.trim() && !Number.isInteger(Number(draft.sortOrder.trim()))) {
    problems.push('The order is a whole number.');
  }
  return problems;
}

function MemberEditor({ member, onChanged }: { member: AdminTeamMember; onChanged: () => void }) {
  const [draft, setDraft] = React.useState<Draft>(() => draftOf(member));
  const [baseline, setBaseline] = React.useState<Draft>(() => draftOf(member));
  const [open, setOpen] = React.useState(false);
  const [confirmSlug, setConfirmSlug] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const set = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const problems = problemsIn(draft);
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  const level = yearsOfExperience(member.careerStartYear);

  const save = useMutation({
    mutationFn: async () => {
      const next: Draft = {
        ...draft,
        name: draft.name.trim(),
        title: draft.title.trim(),
        location: draft.location.trim(),
        careerStartYear: draft.careerStartYear.trim(),
        sortOrder: String(Number(draft.sortOrder.trim() || '0')),
      };
      await api.patch(`/admin/team/${member.id}`, {
        name: next.name,
        title: next.title,
        bio: next.bio,
        careerStartYear: next.careerStartYear ? Number(next.careerStartYear) : null,
        location: next.location || null,
        sortOrder: Number(next.sortOrder),
      });
      return next;
    },
    onSuccess: (next) => {
      setError(null);
      setDraft(next);
      setBaseline(next);
      onChanged();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const togglePublished = useMutation({
    mutationFn: () => api.patch(`/admin/team/${member.id}`, { published: !member.published }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/admin/team/${member.id}`, { confirmSlug: confirmSlug.trim() }),
    onSuccess: onChanged,
    onError: (err) => setError(errorMessage(err)),
  });

  const canSave = dirty && problems.length === 0 && !save.isPending;

  return (
    <Card size="3" className="gb-stack gb-stack--tight">
      {/* The row stays readable with the editor shut, because most visits here are to check
          somebody rather than to change them. */}
      <div className="gb-seat">
        {member.photoUrl ? (
          <img className="gb-credit__face" src={member.photoUrl} alt="" />
        ) : (
          <span className="gb-credit__face gb-credit__face--empty" aria-hidden="true">
            {member.name.slice(0, 1)}
          </span>
        )}

        <div className="gb-stack gb-stack--tight">
          <div className="gb-row gb-row--tight">
            <Text size="2">{member.name}</Text>
            {member.published ? null : <Badge color="gray">{'Hidden'}</Badge>}
            {level !== null ? <Badge color="violet">{`${String(level)} yrs`}</Badge> : null}
            {dirty ? <Badge color="amber">{'Unsaved'}</Badge> : null}
          </div>
          <Text size="1" color="gray">
            {member.title}
          </Text>
          <Text size="1" color="gray" mono>
            {`${member.slug} · ${String(member.credits.length)} ${member.credits.length === 1 ? 'credit' : 'credits'} · edited ${formatRelative(member.updatedAt)}`}
          </Text>
        </div>

        <span className="gb-seat__spacer" />

        <Focusable
          onSelect={() => togglePublished.mutate()}
          selectLabel={member.published ? 'Hide' : 'Show'}
          disabled={togglePublished.isPending}
          className="gb-focusable--flat"
        >
          <Button size="1" variant="surface" disabled={togglePublished.isPending}>
            {member.published ? 'Hide' : 'Show'}
          </Button>
        </Focusable>
        <Focusable
          onSelect={() => setOpen((prev) => !prev)}
          selectLabel={open ? 'Close' : 'Edit'}
          className="gb-focusable--flat"
        >
          <Button size="1" variant={open ? 'solid' : 'surface'}>
            {open ? 'Close' : 'Edit'}
          </Button>
        </Focusable>
      </div>

      {/* What they have been credited on, read-only. It is set on each game and shown here so
          somebody checking a person does not have to open four games to find out. */}
      {member.credits.length > 0 ? (
        <div className="gb-chips">
          {member.credits.map((credit) => (
            <Badge
              key={credit.gameId}
              color="gray"
              title={credit.roles.map((id) => ROLE_BY_ID[id].label).join(', ')}
            >
              {credit.title}
            </Badge>
          ))}
        </div>
      ) : null}

      {open ? (
        <div className="gb-form">
          {error ? (
            <Callout.Root color="danger">
              <Callout.Description>{error}</Callout.Description>
            </Callout.Root>
          ) : null}
          {problems.length > 0 ? (
            <Callout.Root color="amber">
              <Callout.Description>{problems.join(' ')}</Callout.Description>
            </Callout.Root>
          ) : null}

          <div className="gb-two">
            <FocusableTextField
              value={draft.name}
              onChange={(next) => set('name', next)}
              label={'Name'}
              maxLength={80}
            />
            <FocusableTextField
              value={draft.title}
              onChange={(next) => set('title', next)}
              label={'Job title'}
              maxLength={80}
              hint={'What they are. The roles on a credit are what they did on one game.'}
            />
          </div>

          <div className="gb-two">
            <FocusableTextField
              value={draft.careerStartYear}
              onChange={(next) => set('careerStartYear', next)}
              label={'Making games since'}
              placeholder={'2013'}
              type="number"
              maxLength={4}
              hint={'The number on the card is counted from this, so it never goes stale.'}
            />
            <FocusableTextField
              value={draft.location}
              onChange={(next) => set('location', next)}
              label={'Where'}
              placeholder={'Warsaw, Poland'}
              maxLength={80}
              hint={'Optional. Printed under their job title.'}
            />
          </div>

          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.bio}
              onChange={(next) => set('bio', next)}
              label={'The paragraph on their card'}
              placeholder={'Builds the spaces you play in and the rules you play by.'}
              maxLength={1200}
              multiline
              rows={4}
            />
            <Text size="1" color="gray">
              {`What they do, in the studio's voice. ${String(draft.bio.length)} of 1200.`}
            </Text>
          </div>

          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.sortOrder}
              onChange={(next) => set('sortOrder', next)}
              label={'Order on the studio screen'}
              type="number"
            />
            <Text size="1" color="gray">
              {'Lower comes first. People sharing a number fall back to their name.'}
            </Text>
          </div>

          <Portrait member={member} onChanged={onChanged} onError={setError} />

          <div className="gb-row gb-row--tight">
            <span className="gb-seat__spacer" />
            <Focusable
              onSelect={() => setDraft(baseline)}
              selectLabel={'Discard'}
              disabled={!dirty}
              className="gb-focusable--flat"
            >
              <Button size="1" variant="ghost" disabled={!dirty}>
                {'Discard'}
              </Button>
            </Focusable>
            <Focusable onSelect={() => save.mutate()} selectLabel={'Save'} disabled={!canSave}>
              <Button size="2" disabled={!canSave}>
                {save.isPending ? 'Saving' : 'Save'}
              </Button>
            </Focusable>
          </div>

          {/* Removing somebody takes their credits off every game they are on, so the address
              has to be typed back — the same guard deleting a game has. */}
          <div className="gb-stack gb-stack--tight">
            <Eyebrow small tone="quiet">
              {'Remove'}
            </Eyebrow>
            <Text size="1" color="gray">
              {`This takes ${member.name} off the studio screen and off every game they are credited on. Hiding them keeps the credits.`}
            </Text>
            <FocusableTextField
              value={confirmSlug}
              onChange={setConfirmSlug}
              label={`Type ${member.slug} to confirm`}
              maxLength={64}
            />
            <div className="gb-row">
              <Focusable
                onSelect={() => remove.mutate()}
                selectLabel={'Remove'}
                disabled={confirmSlug.trim() !== member.slug || remove.isPending}
              >
                <Button
                  size="1"
                  color="danger"
                  variant="ghost"
                  disabled={confirmSlug.trim() !== member.slug || remove.isPending}
                >
                  {remove.isPending ? 'Removing' : 'Remove from the team'}
                </Button>
              </Focusable>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Their portrait.
 *
 * Saves on the spot rather than waiting for the form's Save, for the reason the game artwork
 * panel gives: an upload is already a round trip, and holding it behind a button would leave a
 * picture on screen that is not yet on the person.
 *
 * Clearing one drops them back to whatever the app ships for their address, and then to their
 * monogram — which is why the button says what will actually happen rather than "Remove".
 */
function Portrait({
  member,
  onChanged,
  onError,
}: {
  member: AdminTeamMember;
  onChanged: () => void;
  onError: (message: string | null) => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);

  const pick = async (file: File) => {
    onError(null);
    if (!ACCEPTED.includes(file.type)) {
      onError('PNG, JPEG, WebP or AVIF.');
      return;
    }

    setBusy(true);
    try {
      // The size is recorded where it can be read, and skipped where it cannot: a portrait has
      // no shape rule to fail, so an unreadable file is not a reason to refuse the upload.
      const size = await readImageSize(file).catch(() => null);
      const imageId = await uploadImage(file, null, size);
      await api.patch(`/admin/team/${member.id}`, { photoImageId: imageId });
      onChanged();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const clear = useMutation({
    mutationFn: () => api.patch(`/admin/team/${member.id}`, { photoImageId: null }),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  return (
    <div className="gb-stack gb-stack--tight">
      <span className="gb-field__label">{'Portrait'}</span>
      <div className="gb-row gb-row--tight">
        <div className="gb-tcg__art gb-tcg__art--thumb">
          {member.photoUrl ? (
            <img className="gb-tcg__photo" src={member.photoUrl} alt="" />
          ) : (
            <span className="gb-tcg__photo gb-tcg__photo--empty" aria-hidden="true">
              {member.name.slice(0, 1)}
            </span>
          )}
        </div>

        <div className="gb-stack gb-stack--tight">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED.join(',')}
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void pick(file);
            }}
          />
          <div className="gb-row gb-row--tight">
            <Focusable
              onSelect={() => inputRef.current?.click()}
              selectLabel={'Upload a portrait'}
              disabled={busy}
              className="gb-focusable--flat"
            >
              <Button size="1" variant="surface" disabled={busy}>
                {busy ? 'Uploading' : member.hasUploadedPhoto ? 'Replace' : 'Upload'}
              </Button>
            </Focusable>
            {member.hasUploadedPhoto ? (
              <Focusable
                onSelect={() => clear.mutate()}
                selectLabel={'Use the bundled one'}
                disabled={clear.isPending}
                className="gb-focusable--flat"
              >
                <Button size="1" variant="ghost" disabled={clear.isPending}>
                  {'Use the app’s own'}
                </Button>
              </Focusable>
            ) : null}
          </div>
          <Text size="1" color="gray">
            {member.hasUploadedPhoto
              ? 'Uploaded. Square art reads best — the card draws it in a 1:1 window.'
              : 'Ships with the app. Uploading one replaces it everywhere without a release.'}
          </Text>
        </div>
      </div>
    </div>
  );
}
